import { useCallback, useMemo, useState, useEffect } from 'react';
import '../../styles/Header.css';
import { getAssetPath, createProductUrl } from '../../utils/paths';
import {
    RiHeart3Line,
    RiSearchLine,
    RiShoppingBag2Line,
    RiUserLine,
    RiMenuLine,
    RiCloseLine,
    RiArrowDownSLine,
    RiArrowRightSLine,
    RiSparkling2Line
} from 'react-icons/ri';
import { Tooltip } from 'react-tooltip'
import { Link } from 'react-router-dom';
import { useLocation, useNavigate } from "react-router-dom";
import { useAccount } from '../../contexts/AccountContext';
import { useApiCall } from '../../hooks/useApiCall';
import { useCounts } from '../../hooks/useCounts';
import { useCatalogType } from '../../contexts/CatalogTypeContext';


function Header({ onClick, style }) {
    const fallbackLogo = getAssetPath("logos/logo_color.png");
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileExpandedCategory, setMobileExpandedCategory] = useState(null);
    const [mobileExpandedSubcategory, setMobileExpandedSubcategory] = useState(null);
    const [hoverTimeout, setHoverTimeout] = useState(null);
    const { account } = useAccount();
    const navigate = useNavigate();
    const location = useLocation();
    const apiCall = useApiCall();
    const { catalogType, catalogOptions, currentCatalog, setCatalogType } = useCatalogType();
    const isCompactCatalogTabs = catalogOptions.length > 0 && catalogOptions.length <= 2;
    const departmentLogo = currentCatalog?.logo_url?.trim();
    const logo = departmentLogo || fallbackLogo;
    const logoLabel = currentCatalog?.label || 'Building Depot';
    const logoTitle = `Building Depot: ${logoLabel}`;

    const closeMobileMenu = useCallback(() => {
        setMobileMenuOpen(false);
        setMobileExpandedCategory(null);
        setMobileExpandedSubcategory(null);
    }, []);

    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [headerNavItems, setHeaderNavItems] = useState([
        { id: 'fallback-about', label: 'About Us', url: '/about-us' },
        { id: 'fallback-contact', label: 'Contact', url: '/contact' },
        { id: 'fallback-projects', label: 'Projects', url: '/projects' }
    ]);
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);
    
    // Use the custom hook for counts
    const { cartCount, wishlistCount } = useCounts();

    const normalizeNavPath = (path) => {
        if (!path) return '/';

        let nextPath = path;
        if (/^https?:\/\//i.test(nextPath)) {
            try {
                nextPath = new URL(nextPath).pathname;
            } catch (e) {
                return '';
            }
        }

        nextPath = nextPath.split('#')[0].split('?')[0];
        if (!nextPath.startsWith('/')) nextPath = `/${nextPath}`;
        return nextPath.length > 1 ? nextPath.replace(/\/+$/, '') : nextPath;
    };

    const isHeaderNavActive = (url) => {
        const targetPath = normalizeNavPath(url);
        const currentPath = normalizeNavPath(location.pathname);
        if (!targetPath) return false;
        if (targetPath === '/') return currentPath === '/';

        if (targetPath === '/projects') {
            return currentPath === '/projects' || currentPath.startsWith('/projects/') || currentPath === '/blogs' || currentPath.startsWith('/blogs/');
        }

        return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
    };


    // Fetch categories on component mount
    useEffect(() => {
        // Create URL-friendly slug from category name
        const createSlug = (name) => {
            return name
                .toLowerCase()
                .replace(/[^a-z0-9\s&]/g, '') // Remove special characters except spaces and &
                .replace(/\s*&\s*/g, '-and-') // Replace & with -and-
                .replace(/\s+/g, '-') // Replace spaces with hyphens
                .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
                .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
        };

        // Transform API categories to match component format
        const transformCategories = (apiCategories) => {
            return apiCategories.map(category => ({
                id: category.id,
                name: category.name,
                slug: createSlug(category.name),
                items: category.children ? transformCategories(category.children) : undefined
            }));
        };

        const fetchCategories = async () => {
            try {
                setCategoriesLoading(true);
                const response = await apiCall('categories', 'POST', { type: catalogType }, null, null);
                
                if (response.success) {
                    // Transform the API response to match the expected format
                    const transformedCategories = transformCategories(response.data.categories);
                    setCategories(transformedCategories);
                } else {
                    console.error('Failed to fetch categories:', response.message);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setCategoriesLoading(false);
            }
        };

        fetchCategories();
    }, [catalogType]);

    // Fetch editable header navigation order
    useEffect(() => {
        apiCall('navigation/header', 'POST', {})
            .then((response) => {
                if (response && response.success && response.data?.items?.length > 0) {
                    setHeaderNavItems(response.data.items);
                }
            })
            .catch((err) => console.error('Error fetching header navigation:', err));
    }, []);

    // Search handler with debouncing
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchQuery.trim().length >= 2) {
                setSearchLoading(true);
                apiCall('products/search', 'POST', { 
                    query: searchQuery,
                    limit: 8,
                    type: catalogType
                }).then((response) => {
                    if (response && response.success) {
                        setSearchResults(response.data.products);
                        setShowSearchResults(true);
                    }
                    setSearchLoading(false);
                });
            } else {
                setSearchResults([]);
                setShowSearchResults(false);
            }
        }, 600); // 300ms debounce

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, catalogType]);

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // Handle Enter key press to navigate to products search page
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter' && searchQuery.trim().length >= 2) {
            navigate(`/products?query=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
            setShowSearchResults(false);
        }
    };

    // Handle search result click
    const handleSearchResultClick = () => {
        setSearchQuery('');
        setShowSearchResults(false);
    };

    // Close dropdown immediately when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.header-categories-wrapper')) {
                if (hoverTimeout) clearTimeout(hoverTimeout);
                setHoverTimeout(null);
                setHoveredCategory(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [hoverTimeout]);

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.header-search-wrapper')) {
                setShowSearchResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!mobileMenuOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') closeMobileMenu();
        };
        const handleResize = () => {
            if (window.innerWidth > 768) closeMobileMenu();
        };

        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', handleResize);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', handleResize);
        };
    }, [mobileMenuOpen, closeMobileMenu]);


    const categoriesView = useMemo( () => {
        const activeCategory = hoveredCategory ? categories.find(cat => cat.id === hoveredCategory) : null;
        const activeSubcategories = activeCategory?.items || [];

        const handleCategoryMouseEnter = (categoryId) => {
            // Clear any existing timeout to prevent closing
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                setHoverTimeout(null);
            }
            // Immediately show the new category
            setHoveredCategory(categoryId);
        };

        const handleWrapperMouseLeave = () => {
            // Add a delay before closing the dropdown when leaving the entire wrapper
            const timeout = setTimeout(() => {
                setHoveredCategory(null);
            }, 600); // 600ms delay
            setHoverTimeout(timeout);
        };

        const handleWrapperMouseEnter = () => {
            // Clear timeout when re-entering the wrapper
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                setHoverTimeout(null);
            }
        };

        return (
            <div 
                className="header-categories-wrapper"
                onMouseLeave={handleWrapperMouseLeave}
                onMouseEnter={handleWrapperMouseEnter}
            >
                {/* Desktop Categories */}
                <div className={`header-categories ${mobileMenuOpen ? 'mobile-hidden' : ''}`}>
                    {categoriesLoading ? (
                        <div className="categories-loading">Loading categories...</div>
                    ) : categories.length > 0 ? (
                        categories.map( (category) => (
                        <Link
                            to={`/products/category/${category.id}/${category.slug}`}
                            key={category.id} 
                            className={`header-category ${hoveredCategory === category.id ? 'active' : ''}`}
                            onMouseEnter={() => handleCategoryMouseEnter(category.id)}
                        >
                            { category.name }
                        </Link>
                        ))
                    ) : (
                        <div className="categories-empty">No categories available</div>
                    )}
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <>
                        <div 
                            className="header-mobile-overlay"
                            onClick={closeMobileMenu}
                        />
                        <div className="header-mobile-menu" role="dialog" aria-modal="true" aria-label="Product categories">
                            <div className="header-mobile-menu-header">
                                <div>
                                    <span className="header-mobile-menu-eyebrow">Browse</span>
                                    <h2>Shop by category</h2>
                                </div>
                                <button
                                    type="button"
                                    className="header-mobile-menu-close"
                                    onClick={closeMobileMenu}
                                    aria-label="Close menu"
                                >
                                    <RiCloseLine />
                                </button>
                            </div>
                            <div className="header-mobile-menu-content" id="mobile-category-menu">
                                {categoriesLoading ? (
                                    <div className="categories-loading">Loading categories...</div>
                                ) : categories.length > 0 ? categories.map((category) => {
                                    const categoryItems = category.items || [];
                                    const isCategoryExpanded = mobileExpandedCategory === category.id;
                                    const categoryPanelId = `mobile-category-${category.id}`;

                                    return (
                                        <div key={category.id} className={`mobile-category-section${isCategoryExpanded ? ' expanded' : ''}`}>
                                            <div className="mobile-category-row">
                                                <Link
                                                    className="mobile-category-title"
                                                    to={`/products/category/${category.id}/${category.slug}`}
                                                    onClick={closeMobileMenu}
                                                >
                                                    {category.name}
                                                </Link>
                                                {categoryItems.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="mobile-category-toggle"
                                                        onClick={() => {
                                                            setMobileExpandedCategory(isCategoryExpanded ? null : category.id);
                                                            setMobileExpandedSubcategory(null);
                                                        }}
                                                        aria-expanded={isCategoryExpanded}
                                                        aria-controls={categoryPanelId}
                                                        aria-label={`${isCategoryExpanded ? 'Collapse' : 'Expand'} ${category.name}`}
                                                    >
                                                        {isCategoryExpanded ? <RiArrowDownSLine /> : <RiArrowRightSLine />}
                                                    </button>
                                                )}
                                            </div>

                                            {isCategoryExpanded && categoryItems.length > 0 && (
                                                <div className="mobile-subcategories" id={categoryPanelId}>
                                                    {categoryItems.map((subCategory) => {
                                                        const subCategoryItems = subCategory.items || [];
                                                        const subCategoryKey = `${category.id}-${subCategory.id}`;
                                                        const isSubcategoryExpanded = mobileExpandedSubcategory === subCategoryKey;
                                                        const subcategoryPanelId = `mobile-subcategory-${subCategoryKey}`;

                                                        return (
                                                            <div key={subCategory.id} className="mobile-subcategory">
                                                                <div className="mobile-subcategory-row">
                                                                    <Link
                                                                        className="mobile-subcategory-title"
                                                                        to={`/products/category/${subCategory.id}/${subCategory.slug}`}
                                                                        onClick={closeMobileMenu}
                                                                    >
                                                                        {subCategory.name}
                                                                    </Link>
                                                                    {subCategoryItems.length > 0 && (
                                                                        <button
                                                                            type="button"
                                                                            className="mobile-subcategory-toggle"
                                                                            onClick={() => setMobileExpandedSubcategory(isSubcategoryExpanded ? null : subCategoryKey)}
                                                                            aria-expanded={isSubcategoryExpanded}
                                                                            aria-controls={subcategoryPanelId}
                                                                            aria-label={`${isSubcategoryExpanded ? 'Collapse' : 'Expand'} ${subCategory.name}`}
                                                                        >
                                                                            {isSubcategoryExpanded ? <RiArrowDownSLine /> : <RiArrowRightSLine />}
                                                                        </button>
                                                                    )}
                                                                </div>

                                                                {isSubcategoryExpanded && subCategoryItems.length > 0 && (
                                                                    <div className="mobile-sub-items" id={subcategoryPanelId}>
                                                                        {subCategoryItems.map((item) => (
                                                                            <Link
                                                                                key={item.id}
                                                                                className="mobile-sub-item"
                                                                                to={`/products/category/${item.id}/${item.slug}`}
                                                                                onClick={closeMobileMenu}
                                                                            >
                                                                                <span>{item.name}</span>
                                                                                <RiArrowRightSLine aria-hidden="true" />
                                                                            </Link>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }) : (
                                    <div className="categories-empty">No categories available</div>
                                )}
                            </div>
                        </div>
                    </>
                )}
                
                {/* Dropdown for subcategories */}
                {hoveredCategory && activeSubcategories.length > 0 && (
                    <div 
                        className="header-dropdown"
                        onClick={() => {
                            if (hoverTimeout) clearTimeout(hoverTimeout);
                            setHoverTimeout(null);
                            setHoveredCategory(null);
                        }}
                    >
                        <div className="header-dropdown-content">
                            {activeSubcategories.map((subCategory) => (
                                    <div key={subCategory.id} className="header-dropdown-column">
                                        <Link to={`/products/category/${subCategory.id}/${subCategory.slug}`} className="header-dropdown-title">
                                            {subCategory.name}
                                            <span className="header-dropdown-arrow">→</span>
                                        </Link>
                                        {subCategory.items && (
                                            <div className="header-dropdown-items">
                                                {subCategory.items.map((subItem) => (
                                                    <Link 
                                                        key={subItem.id} 
                                                        className="header-dropdown-item"
                                                        to={`/products/category/${subItem.id}/${subItem.slug}`}
                                                    >
                                                        {subItem.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        )

    }, [categories, hoveredCategory, mobileMenuOpen, mobileExpandedCategory, mobileExpandedSubcategory, categoriesLoading, hoverTimeout, closeMobileMenu])

  return (
    <div className="header-wrapper" style={style}>
        <Tooltip id="header-tooltip" />

        <div className="header-sub">
            <div className="header-sub-content">
                <div className={`catalog-type-tabs ${isCompactCatalogTabs ? 'compact' : ''}`} aria-label="Catalog department">
                    {catalogOptions.map((type) => (
                        <button
                            key={type.id}
                            type="button"
                            className={`catalog-type-tab ${catalogType === type.id ? 'active' : ''}`}
                            onClick={() => {
                                closeMobileMenu();
                                setCatalogType(type.id);
                                navigate('/');
                            }}
                        >
                            {type.label}
                        </button>
                    ))}
                </div>
                
                {headerNavItems.map((item) => {
                    const isActive = isHeaderNavActive(item.url);
                    return (
                    <Link
                        key={item.id}
                        to={item.url}
                        className={`header-sub-link${isActive ? ' active' : ''}`}
                        aria-current={isActive ? 'page' : undefined}
                    >
                        {item.label}
                    </Link>
                    );
                })}
            </div>

        </div>
        <div className="header-sticky">
            <div className="header">

                <Link to="/" className="header-logo">
                    <img
                        src={logo}
                        alt={`${logoLabel} logo`}
                        title={logoTitle}
                        onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = fallbackLogo;
                        }}
                    />
                </Link>

                <div className="header-search-wrapper">

                    <div className="header-search">
                        <button className="header-search-button" title="Search">
                            <RiSearchLine size={18} />
                        </button>
                        <input 
                            type="text" 
                            className="header-search-input" 
                            placeholder="Search products, categories..." 
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                            onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
                        />
                    </div>

                    {/* Search Results Dropdown */}
                    {showSearchResults && searchResults.length > 0 && (
                        <div className="header-search-results">
                            {searchResults.map((product) => (
                                <Link
                                    key={product.id}
                                    className="header-search-result-item"
                                    to={createProductUrl(product.id, product.name)}
                                    onClick={handleSearchResultClick}
                                >
                                    {product.image ? (
                                        <img 
                                            src={product.image} 
                                            alt={product.name}
                                            className="search-result-image"
                                        />
                                    ) : (
                                        <div className="search-result-image search-result-image-empty">No Image</div>
                                    )}
                                    <div className="search-result-details">
                                        <div className="search-result-name">{product.name}</div>
                                        {product.brand && (
                                            <div className="search-result-brand">{product.brand}</div>
                                        )}
                                        <div className="search-result-price">
                                            {product.sale_price > 0 ? (
                                                <>
                                                    <span className="price-sale">{product.price.curr} {parseFloat(product.sale_price).toFixed(2)}</span>
                                                    <span className="price-original">{product.price.curr} {parseFloat(product.price.amount).toFixed(2)}</span>
                                                </>
                                            ) : (
                                                <span className="price">{product.price.curr} {parseFloat(product.price.amount).toFixed(2)}</span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}

                    {showSearchResults && searchQuery.trim().length >= 2 && searchResults.length === 0 && !searchLoading && (
                        <div className="header-search-results">
                            <div className="header-search-no-results">
                                No products found for "{searchQuery}"
                            </div>
                        </div>
                    )}

                </div>
                    
                { (account?.admin) &&
                <Link to="/experience" className="header-showroom-btn" title="Virtual Showroom" data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content="Enter the Virtual Showroom">
                    <RiSparkling2Line size={16} />
                    Showroom
                </Link>
                }
                
                <div className="header-account">
                    {/* <button onClick={toggleTheme} className="dark-mode-toggle" title={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content={isDark ? 'Light mode' : 'Dark mode'}>
                        {isDark ? <RiSunLine size={20} /> : <RiMoonLine size={20} />}
                    </button> */}
                    <Link to={ account ? '/account' : '/sign-in'} className="header-account-button" title={ account ? 'Account' : 'Sign in'} data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content={ account ? "Manage your account" : "Sign in to your account"}>
                        { account ?
                        <div className='header-account-avatar' style={{backgroundImage: `url(${account.avatar || getAssetPath('default_avatar.png')})`}}></div>
                        :
                        <RiUserLine size={20} />
                        }
                    </Link>
                    <Link to='/wishlist' className="header-account-button" title={'Wishlist'} data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content="View your wishlist" style={{ position: 'relative' }}>
                        <RiHeart3Line size={20} />
                        {wishlistCount > 0 && (
                            <span className="header-badge">{wishlistCount}</span>
                        )}
                    </Link>
                    <Link to='/cart' className="header-account-button" title={'Shopping cart'} data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content="View your shopping cart" style={{ position: 'relative' }}>
                        <RiShoppingBag2Line size={20} />
                        {cartCount > 0 && (
                            <span className="header-badge">{cartCount}</span>
                        )}
                    </Link>
                    
                    {/* Mobile menu toggle - rightmost position */}
                    <button 
                        className="header-mobile-toggle"
                        onClick={() => mobileMenuOpen ? closeMobileMenu() : setMobileMenuOpen(true)}
                        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={mobileMenuOpen}
                        aria-controls="mobile-category-menu"
                    >
                        {mobileMenuOpen ? <RiCloseLine /> : <RiMenuLine />}
                    </button>
                </div>
            
            </div>

            { categoriesView }
        </div>
    </div>
  );
}

export default Header;
